import { 
  Component, 
  OnInit, 
  OnDestroy, 
  ChangeDetectionStrategy, 
  ChangeDetectorRef,
  DestroyRef,
  inject
} from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { interval, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartType } from 'chart.js';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';

import { 
  MonitoringService, 
  SystemHealthMetrics, 
  DatabaseHealthMetrics,
  HistoryPoint,
  DatabaseHistoryPoint,
  RefreshIntervalOption,
  REFRESH_INTERVALS
} from '../../../services/monitoring.service';
import { BackendLogsPanelComponent } from '../backend-logs/backend-logs-panel/backend-logs-panel.component';

// Chart.js komponensek regisztrálása
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
);

@Component({
  selector: 'admin-monitoring',
  standalone: true,
  imports: [
    CommonModule, 
    MatIconModule, 
    MatProgressBarModule, 
    MatTooltipModule,
    DecimalPipe,
    BaseChartDirective,
    BackendLogsPanelComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './admin-monitoring.component.html',
  styleUrls: ['./admin-monitoring.component.scss']
})
export class AdminMonitoringComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  private refreshSubscription?: Subscription;
  
  // Adatok
  systemHealth: SystemHealthMetrics | null = null;
  databaseHealth: DatabaseHealthMetrics | null = null;
  
  // Állapotok
  isLoading = true;
  errorMessage: string | null = null;
  lastRefresh: Date | null = null;
  
  // Dinamikus frissítési intervallum
  refreshIntervals = REFRESH_INTERVALS;
  selectedInterval: RefreshIntervalOption = REFRESH_INTERVALS[1]; // 5 mp alapértelmezett

  // Chart konfigurációk
  chartType: ChartType = 'line';

  // CPU + Memória chart
  cpuMemChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'CPU %',
        borderColor: '#ff6384',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      },
      {
        data: [],
        label: 'Memória %',
        borderColor: '#36a2eb',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      }
    ]
  };

  cpuMemChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    scales: {
      y: { min: 0, max: 100, ticks: { callback: (v) => v + '%' } },
      x: { ticks: { maxTicksLimit: 10, maxRotation: 0 } }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  // Disk chart
  diskChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Disk %',
        borderColor: '#ff9f40',
        backgroundColor: 'rgba(255, 159, 64, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      }
    ]
  };

  diskChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    scales: {
      y: { min: 0, max: 100, ticks: { callback: (v) => v + '%' } },
      x: { ticks: { maxTicksLimit: 10, maxRotation: 0 } }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  // Hálózat chart
  networkChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Küldött (MB)',
        borderColor: '#4bc0c0',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      },
      {
        data: [],
        label: 'Fogadott (MB)',
        borderColor: '#9966ff',
        backgroundColor: 'rgba(153, 102, 255, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      }
    ]
  };

  networkChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    scales: {
      y: { min: 0, ticks: { callback: (v) => v + ' MB' } },
      x: { ticks: { maxTicksLimit: 10, maxRotation: 0 } }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  // Adatbázis kapcsolatok chart
  dbChartData: ChartConfiguration<'line'>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: 'Összes kapcsolat',
        borderColor: '#36a2eb',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      },
      {
        data: [],
        label: 'Aktív lekérdezés',
        borderColor: '#ff6384',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 0
      }
    ]
  };

  dbChartOptions: ChartConfiguration<'line'>['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    scales: {
      y: { min: 0 },
      x: { ticks: { maxTicksLimit: 10, maxRotation: 0 } }
    },
    plugins: {
      legend: { position: 'top' },
      tooltip: { mode: 'index', intersect: false }
    }
  };

  constructor(
    private monitoringService: MonitoringService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadAllMetrics();
    this.startAutoRefresh();
  }

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  /**
   * Frissítési intervallum váltása
   */
  setRefreshInterval(option: RefreshIntervalOption): void {
    this.selectedInterval = option;
    this.stopAutoRefresh();
    this.startAutoRefresh();
    this.cdr.markForCheck();
  }

  /**
   * Összes metrika betöltése (aktuális + history)
   */
  loadAllMetrics(): void {
    this.errorMessage = null;
    
    // Rendszer history (tartalmazza a current-et is)
    this.monitoringService.getSystemHealthHistory(120)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.systemHealth = response.current;
          this.lastRefresh = new Date();
          this.isLoading = false;
          this.updateSystemCharts(response.history);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Rendszer metrikák betöltési hiba:', err);
          this.errorMessage = err.message || 'Rendszer metrikák betöltése sikertelen';
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });

    // Adatbázis history (tartalmazza a current-et is)
    this.monitoringService.getDatabaseHealthHistory(120)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.databaseHealth = response.current;
          this.updateDatabaseChart(response.history);
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Adatbázis metrikák betöltési hiba:', err);
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Rendszer chart-ok frissítése history adatokból
   */
  private updateSystemCharts(history: HistoryPoint[]): void {
    const labels = history.map(h => {
      const d = new Date(h.timestamp);
      return d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    });

    // CPU + Memória chart
    this.cpuMemChartData = {
      ...this.cpuMemChartData,
      labels,
      datasets: [
        { ...this.cpuMemChartData.datasets[0], data: history.map(h => h.cpu_percent) },
        { ...this.cpuMemChartData.datasets[1], data: history.map(h => h.memory_percent) }
      ]
    };

    // Disk chart
    this.diskChartData = {
      ...this.diskChartData,
      labels,
      datasets: [
        { ...this.diskChartData.datasets[0], data: history.map(h => h.disk_percent) }
      ]
    };

    // Hálózat chart
    this.networkChartData = {
      ...this.networkChartData,
      labels,
      datasets: [
        { ...this.networkChartData.datasets[0], data: history.map(h => h.network_sent_mb) },
        { ...this.networkChartData.datasets[1], data: history.map(h => h.network_recv_mb) }
      ]
    };
  }

  /**
   * Adatbázis chart frissítése history adatokból
   */
  private updateDatabaseChart(history: DatabaseHistoryPoint[]): void {
    const labels = history.map(h => {
      const d = new Date(h.timestamp);
      return d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    });

    this.dbChartData = {
      ...this.dbChartData,
      labels,
      datasets: [
        { ...this.dbChartData.datasets[0], data: history.map(h => h.total_connections) },
        { ...this.dbChartData.datasets[1], data: history.map(h => h.active_connections) }
      ]
    };
  }

  /**
   * Automatikus frissítés indítása
   */
  private startAutoRefresh(): void {
    this.refreshSubscription = interval(this.selectedInterval.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadAllMetrics();
      });
  }

  /**
   * Automatikus frissítés leállítása
   */
  private stopAutoRefresh(): void {
    if (this.refreshSubscription) {
      this.refreshSubscription.unsubscribe();
    }
  }

  /**
   * Manuális frissítés
   */
  refreshMetrics(): void {
    this.loadAllMetrics();
  }

  /**
   * Színosztály meghatározása a százalék alapján
   */
  getStatusClass(percent: number): string {
    if (percent < 60) return 'status-good';
    if (percent < 80) return 'status-warning';
    return 'status-critical';
  }

  /**
   * Státusz ikon meghatározása a százalék alapján
   */
  getStatusIcon(percent: number): string {
    if (percent < 60) return 'check_circle';
    if (percent < 80) return 'warning';
    return 'error';
  }

  /**
   * Progress bar szín meghatározása
   */
  getProgressBarColor(percent: number): 'primary' | 'accent' | 'warn' {
    if (percent < 60) return 'primary';
    if (percent < 80) return 'accent';
    return 'warn';
  }

  /**
   * MB -> GB konvertálás és formázás
   */
  formatMbToGb(mb: number): string {
    if (mb >= 1024) {
      return (mb / 1024).toFixed(2) + ' GB';
    }
    return mb.toFixed(1) + ' MB';
  }

  /**
   * Hálózati forgalom formázás
   */
  formatNetworkTraffic(mb: number): string {
    if (mb >= 1024) {
      return (mb / 1024).toFixed(2) + ' GB';
    }
    return mb.toFixed(1) + ' MB';
  }
}
